import { Box, TextField, Typography } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import {
  ControllerAutoComplete,
  ControllerDatePicker,
  CustomCheckbox,
  FormSection,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useIsDesktop } from 'Hooks';
import { useContext, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import { textFieldShrink } from 'Utils';
import { validateLettersAndNumbersOnly } from 'Utils/validateLettersAndNumbersOnly';
import { IndexContext } from '../CaseContext';
import { validateNumbersOnly } from 'Utils/validateNumbersOnly';
import { styleTextField } from 'Theme';
import { maxFutureDate } from 'Constants';

export const IDF_TYPES = [1, 2, 3];
export const IDF_RANKS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 23,
  24, 44,
];

export const POLICE_TYPES = [4, 5, 6];
export const POLICE_RANKS = [
  23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
];

export const SHABAS_TYPES = 7;
export const SHABAS_RANKS = [
  14, 15, 16, 17, 21, 23, 24, 41, 42, 43, 50, 72, 73, 74,
];

export const FirstSection = () => {
  const [serviceTypes, militaryTroops] = useAppSelector(
    ({
      generalContent: {
        generalContent: { serviceTypes, militaryTroops },
      },
    }) => [serviceTypes, militaryTroops]
  );

  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    clearErrors,
  } = useFormContext<ICaseDetailsInputs>();

  const { index } = useContext(IndexContext);

  const isDesktop = useIsDesktop();

  const dictionary = Dictionary[DICTIONARY.CASE_DETAILS];

  const baseName = `caseDetails.${index}.militaryServiceSection`;
  const otherServiceTypeId = 12;
  const showTroopId = [1, 2, 3];

  const serviceTypeId = useWatch({
    name: `${baseName}.serviceTypeId` as const,
    defaultValue: 0,
  });

  const [hasNoReleaseDate, hasNoMedicalProfile, hasNoTroop, medicalProfile] =
    useWatch({
      name: [
        `${baseName}.hasNoReleaseDate`,
        `${baseName}.hasNoMedicalProfile`,
        `${baseName}.hasNoTroop`,
        `${baseName}.medicalProfile`,
      ],
    });

  const militaryServiceSectionValues = getValues(
    `caseDetails.${index}.militaryServiceSection`
  );

  const militaryServiceError = errors.caseDetails
    ? errors.caseDetails[index]?.militaryServiceSection
    : undefined;

  useEffect(() => {
    if (hasNoReleaseDate) {
      setValue(
        `${baseName}.releaseDate` as `caseDetails.0.militaryServiceSection.releaseDate`,
        null
      );
      clearErrors(
        `${baseName}.releaseDate` as `caseDetails.0.militaryServiceSection.releaseDate`
      );
    }
    if (hasNoTroop) {
      setValue(
        `${baseName}.troopId` as `caseDetails.0.militaryServiceSection.troopId`,
        null
      );
      clearErrors(
        `${baseName}.troopId` as `caseDetails.0.militaryServiceSection.troopId`
      );
    }
    if (hasNoMedicalProfile) {
      setValue(
        `${baseName}.medicalProfile` as `caseDetails.0.militaryServiceSection.medicalProfile`,
        ''
      );
      clearErrors(
        `${baseName}.medicalProfile` as `caseDetails.0.militaryServiceSection.medicalProfile`
      );
    }
  }, [
    clearErrors,
    setValue,
    baseName,
    hasNoReleaseDate,
    hasNoTroop,
    hasNoMedicalProfile,
  ]);

  return (
    <FormSection grouped id={dictionary.firstSectionTitle}>
      <Typography
        fontSize={isDesktop ? '18px !important' : '16px !important'}
        sx={{ fontWeight: 'bold', mb: 2 }}
        id={dictionary.firstSectionTitle}
      >
        {dictionary.firstSectionTitle}
      </Typography>

      <Box m={'10px 0'}>
        <ControllerAutoComplete
          options={serviceTypes}
          name={`${baseName}.serviceTypeId` as const}
          label={dictionary.firstSectionFirstInput}
        />
      </Box>
      {serviceTypeId === otherServiceTypeId && (
        <Box m={'10px 0'}>
          <TextField
            role={
              Boolean(militaryServiceError?.otherServiceType)
                ? 'alert'
                : undefined
            }
            inputProps={{
              maxLength: '50',
            }}
            id={`${dictionary.serviceTypeDetail}-${index}`}
            {...register(
              `${baseName}.otherServiceType` as `caseDetails.0.militaryServiceSection.otherServiceType`
            )}
            {...textFieldShrink(militaryServiceSectionValues?.otherServiceType)}
            error={Boolean(militaryServiceError?.otherServiceType)}
            helperText={
              Boolean(militaryServiceError?.otherServiceType?.message) &&
              generateError(
                dictionary.serviceTypeDetail,
                militaryServiceError?.otherServiceType?.message!
              )
            }
            label={dictionary.serviceTypeDetail}
            onChange={(event: any) => validateLettersAndNumbersOnly(event)}
            sx={styleTextField}
          />
        </Box>
      )}
      {serviceTypeId > 0 && (
        <Box m={'10px 0'}>
          <ControllerDatePicker
            name={
              `${baseName}.releaseDate` as `caseDetails.0.militaryServiceSection.releaseDate`
            }
            label={dictionary.firstSectionDateInput}
            disabled={militaryServiceSectionValues?.hasNoReleaseDate}
            maxDate={maxFutureDate}
          />
          <CustomCheckbox
            name={
              `${baseName}.hasNoReleaseDate` as `caseDetails.0.militaryServiceSection.hasNoReleaseDate`
            }
            label={dictionary.noInformationOrRemember}
          />
        </Box>
      )}
      <Box m={'10px 0'}>
        <TextField
          role={Boolean(militaryServiceError?.role) ? 'alert' : undefined}
          inputProps={{
            maxLength: '50',
          }}
          id={`${dictionary.firstSectionSecondInput}-${index}`}
          {...textFieldShrink(militaryServiceSectionValues?.role, true)}
          {...register(
            `${baseName}.role` as `caseDetails.0.militaryServiceSection.role`
          )}
          error={Boolean(militaryServiceError?.role)}
          helperText={
            Boolean(militaryServiceError?.role?.message) &&
            generateError(
              dictionary.firstSectionSecondInput,
              militaryServiceError?.role?.message!
            )
          }
          label={dictionary.firstSectionSecondInput}
          onChange={(event: any) => validateLettersAndNumbersOnly(event)}
          sx={styleTextField}
        />
      </Box>
      {showTroopId.includes(serviceTypeId) && (
        <Box m={'10px 0'}>
          <ControllerAutoComplete
            name={
              `${baseName}.troopId` as `caseDetails.0.militaryServiceSection.troopId`
            }
            label={dictionary.firstSectionFourthInput}
            options={militaryTroops}
            disabled={hasNoTroop}
          />
          <CustomCheckbox
            name={
              `${baseName}.hasNoTroop` as `caseDetails.0.militaryServiceSection.hasNoTroop`
            }
            label={dictionary.noInformationRegardingCorps}
          />
        </Box>
      )}
      <Box m={'10px 0 20px 0'}>
        <TextField
          role={
            Boolean(militaryServiceError?.medicalProfile) ? 'alert' : undefined
          }
          inputProps={{
            maxLength: '2',
          }}
          id={`${dictionary.firstSectionSixthInput}-${index}`}
          {...textFieldShrink(medicalProfile)}
          error={Boolean(militaryServiceError?.medicalProfile)}
          helperText={
            Boolean(militaryServiceError?.medicalProfile?.message) &&
            generateError(
              dictionary.firstSectionSixthInput,
              militaryServiceError?.medicalProfile?.message!
            )
          }
          {...register(
            `${baseName}.medicalProfile` as `caseDetails.0.militaryServiceSection.medicalProfile`
          )}
          label={dictionary.firstSectionSixthInput}
          onKeyDown={(event) => validateNumbersOnly(event)}
          disabled={militaryServiceSectionValues?.hasNoMedicalProfile}
          sx={styleTextField}
        />
        <CustomCheckbox
          name={
            `${baseName}.hasNoMedicalProfile` as `caseDetails.0.militaryServiceSection.hasNoMedicalProfile`
          }
          label={dictionary.noHealthProfile}
        />
      </Box>
    </FormSection>
  );
};
